import { httpService, getSavedLang } from '@flexo/general';
import type { DeviceInfo } from '@capacitor/device';

const ENV = (import.meta as any).env;
const savedLang = getSavedLang();
const browserLanguage = savedLang?.split('_')[1] || navigator.language.split('-')[0] || ENV.VITE_APP_LOCALE;

export interface SubmitFeedbackParams {
  ENV: any;
  metadata: any;
  version: string;
  inputData: string;
  feedback: number | null;
  user: any;
  getDeviceInfo: () => Promise<DeviceInfo | 'Not available'>;
  parseIosFromUserAgent: () => {
    model?: string;
    osName?: string;
    osVersion?: string;
  };
  dispatch: any;
  ALERT: any;
  RIGHT_DRAWER?: any;
}

export async function submitFeedback(
  params: SubmitFeedbackParams
): Promise<void> {
  const {
    ENV,
    metadata,
    version,
    inputData,
    feedback,
    user,
    getDeviceInfo,
    parseIosFromUserAgent,
    dispatch,
    ALERT,
    RIGHT_DRAWER,
  } = params;

  // Note: Authentication is now handled automatically by httpService via Clerk.
  // The httpService interceptor will auto-inject the Clerk token if configured.

  const info: any = await getDeviceInfo();
  const isInfoAvailable = info !== 'Not available' && !!info;
  const uaInfo = parseIosFromUserAgent();

  let deviceIdUuid: string | undefined;
  try {
    const { Device } = await import('@capacitor/device');
    const id = await Device.getId();
    deviceIdUuid = (id as any)?.uuid;
  } catch( error ) {
    console.warn('Device ID not available');
  }

  let platformFallback = 'web';
  try {
    const { Capacitor } = await import('@capacitor/core');
    platformFallback = Capacitor.getPlatform();
  } catch {
    console.warn('Platform not available');
  }

  const org = (ENV?.VITE_APP_ORG || '').toString();
  let url = '';
  let data: any = {};

  function findCommunity() {
    if (metadata?.selectedEntity?.memberId) {
      const member =
        metadata?.entities?.member?.find(
          (member: any) =>
            member?.memberId === metadata?.selectedEntity?.memberId
        ) || null;
      const memberCommunity =
        metadata?.entities?.community?.find(
          (community: any) => community?.communityId === member?.communityId
        ) || null;
      return {
        communityId: memberCommunity?.communityId || null,
        communityExternalId:
          memberCommunity?.communityExternalIdexternalId || null,
      };
    }
    if (metadata?.selectedEntity?.siteId) {
      const site =
        metadata?.entities?.site?.find(
          (site: any) => site?.siteId === metadata?.selectedEntity?.siteId
        ) || null;
      const siteCommunity =
        metadata?.entities?.community?.find(
          (community: any) => community?.communityId === site?.communityId
        ) || null;
      return {
        communityId: siteCommunity?.communityId || null,
        communityExternalId:
          siteCommunity?.communityExternalIdexternalId || null,
      };
    }
    return null;
  }

  // Common context used by specific org payloads and mail body
  const communityIdFromSelection =
    metadata?.selectedEntity?.communityId || null;
  const communityExternalIdFromSelection =
    metadata?.entities?.communities?.find(
      (comm) => comm.communityId === communityIdFromSelection
    )?.communityExternalId || null;

  const findPod = () => {
    if (metadata?.selectedEntity?.pod) return metadata?.selectedEntity?.pod;
    if (metadata?.selectedSiteId) {
      return metadata?.entities?.site?.find(
        (site) => site.siteId === metadata?.selectedSiteId
      )?.pod;
    }
    return 'No POD selected';
  };
  const common = {
    appVersion: version || ENV.VITE_APP_VERSION || 'N/A',
    channel: ENV.VITE_APP_IS_WEB === 'true' ? 'manager' : 'app',
    communityId: communityIdFromSelection || findCommunity()?.communityId|| 'Unknown',
    communityExternalId:
      communityExternalIdFromSelection || findCommunity()?.communityExternalId|| 'Unknown',
    deviceId: deviceIdUuid ?? 'Unknown',
    deviceInformation: JSON.stringify(info),
    model:
      isInfoAvailable && (info as any).model
        ? (info as any).model
        : uaInfo.model ?? 'Unknown',
    os:
      isInfoAvailable && (info as any).operatingSystem
        ? (info as any).operatingSystem
        : uaInfo.osName ?? 'Unknown',
    osVersion:
      isInfoAvailable && (info as any).osVersion
        ? (info as any).osVersion
        : uaInfo.osVersion ?? 'Unknown',
    platform: isInfoAvailable
      ? (info as any).platform ?? platformFallback
      : platformFallback,
    webViewVersion:
      isInfoAvailable && (info as any).webViewVersion
        ? (info as any).webViewVersion
        : platformFallback === 'android'
        ? 'Unknown'
        : 'Not available on iOS',
    rating: feedback,
    selectedPod: findPod() || 'No POD selected',
    userEmail:
      user?.email ||
      user?.tokenContent?.email ||
      'Unknown',
  };

  // Default endpoint and data
  const defaultUrl = `${ENV.VITE_APP_BASE_URL}/auth/v1_1/feedback`;
  const defaultMailMessage = `
    <strong>Org name:</strong> ${ENV.VITE_APP_ORG || 'N/A'} <br/>
    <strong>App version:</strong> ${common.appVersion} <br/>
    <strong>Feedback:</strong> ${inputData || ''} <br/>
    <strong>Currently selected POD:</strong> ${common.selectedPod} <br/>
    <strong>Device information:</strong> <br/>
    <ul style="margin: 10px 0; padding-left: 20px;">
      <li><strong>Platform:</strong> ${common.platform}</li>  <br/>
      <li><strong>Model:</strong> ${common.model}</li> <br/>
      <li><strong>OS:</strong> ${common.os}</li> <br/>
      <li><strong>OS version:</strong> ${common.osVersion}</li> <br/>
      <li><strong>Web view version:</strong> ${common.webViewVersion}</li> <br/>
      <li><strong>Device ID:</strong> ${common.deviceId}</li> <br/>
    </ul>
    <br/>
    <strong>Rating:</strong> ${common.rating} <br/>
  `;

  switch (org) {
    // Extend here for org-specific routes and payloads
    // case 'your-org':
    //   url = `${ENV.VITE_APP_BASE_URL}/custom/feedback`;
    //   data = { ...defaultData, org };
    //   break;
    case 'kwick':
      url = `/custom/kwick/v1/createTicket`;
      console.log('_common', common);
      data = {
        app_version: common.appVersion,
        channel: common.channel,
        community_id: common.communityId,
        community_external_id: common.communityExternalId,
        device_id: common.deviceId,
        device_information: common.deviceInformation,
        feedback: inputData,
        model: common.model,
        os: common.os,
        os_version: common.osVersion,
        platform: common.platform,
        rating: common.rating,
        selected_pod: common.selectedPod,
        user_email: common.userEmail,
        web_version: common.webViewVersion,
      };
      break;
    default:
      url = defaultUrl;
      data = {
        user: common.userEmail,
        message: defaultMailMessage,
      };
      break;
  }

  try {
    // httpService automatically injects Clerk auth token via interceptor
    await httpService.post({ url, data });

  } catch (error) {
    if (ENV?.VITE_APP_IS_WEB === 'true' && RIGHT_DRAWER) {
      dispatch(RIGHT_DRAWER.ResetDrawer());
    }

    dispatch(
      ALERT.SetAlert({
        value: { type: 'BANNER_UNEXPECTED_ERROR' },
      } as any)
    );
    throw error;
  }
}
